import { Switch } from '@civilgrid/ui/components';
import { useEffect, useState } from 'react';
import { updateOrgConnectbaseLookup } from '~/api/internal';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';

interface Props {
  organizationId: string;
  /** The organization's stored flag, as last loaded from the server. */
  connectbaseLookupEnabled: boolean;
}

/**
 * Per-org switch for Connectbase provider lookups. Saves on its own rather than through the
 * Feature Provisioning form: that form's submit archives the active subscription and inserts a
 * replacement row, which a lookup toggle has no business doing.
 */
export default function ConnectbaseLookupToggle({ organizationId, connectbaseLookupEnabled }: Props) {
  const [enabled, setEnabled] = useState(connectbaseLookupEnabled);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEnabled(connectbaseLookupEnabled);
  }, [connectbaseLookupEnabled]);

  const handleChange = async (nextEnabled: boolean) => {
    const previous = enabled;
    setEnabled(nextEnabled);
    setIsSaving(true);
    try {
      const saved = await updateOrgConnectbaseLookup(organizationId, nextEnabled);
      setEnabled(saved);
      capturePosthogEvent(POSTHOG_EVENTS.organizationConnectbaseLookupToggled, {
        organization_id: organizationId,
        enabled: saved,
      });
    } catch (_err) {
      setEnabled(previous);
      Notify.error({ message: 'Error updating Connectbase lookup' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-grey-30 p-4" data-slot="connectbase-lookup-toggle">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-bold">Connectbase Lookup</div>
          <div className="text-sm text-grey-90">
            When off, no Connectbase provider or fiber-lit building lookups run for this organization's projects — on
            creation, on boundary edits, or on recompute. Background jobs pick the change up within a minute.
          </div>
        </div>
        <div className="mt-0.5 flex shrink-0 items-center gap-2">
          <span className={`text-sm ${enabled ? 'text-grey-50' : 'text-grey-90 font-semibold'}`}>Off</span>
          <Switch
            data-testid="connectbase-lookup-toggle"
            aria-label="Enable Connectbase lookup"
            checked={enabled}
            disabled={isSaving}
            onCheckedChange={handleChange}
          />
          <span className={`text-sm ${enabled ? 'text-grey-90 font-semibold' : 'text-grey-50'}`}>On</span>
        </div>
      </div>
    </div>
  );
}
